export type TimeEntrySource = 'CLOCK' | 'TIMER' | 'MANUAL' | 'SESSION' | 'MEETING' | 'FOCUS'

export type TimeEntryStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'LOCKED'

export const TIME_ENTRY_BUCKETS = [
  'CLIENT_BILLABLE',
  'FIXED_BID',
  'CLIENT_NON_BILLABLE',
  'INTERNAL',
] as const

export type TimeEntryBucket = (typeof TIME_ENTRY_BUCKETS)[number]

export const CLIENT_BILLING_MODES = [
  'HOURLY',
  'FIXED_BID',
  'NON_BILLABLE',
] as const

export type ClientBillingMode = (typeof CLIENT_BILLING_MODES)[number]

export type TimeEntryBucketSource = 'DERIVED' | 'OVERRIDE'

export type TimesheetWeekStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'LOCKED'

export type TimesheetWeekStart = 'MONDAY' | 'SUNDAY'

export type TimesheetRounding = 'NONE' | '5_MIN' | '6_MIN' | '15_MIN'

export interface TimesheetAutoSources {
  sessions: boolean
  meetings: boolean
  focus: boolean
}

export interface TimesheetOvertimeSettings {
  dailyMinutes: number
  weeklyMinutes: number
  multiplier: number
}

export interface TimesheetRemindersSettings {
  submitReminder: boolean
  approvalReminder: boolean
}

export interface TimesheetSettings {
  autoSources: TimesheetAutoSources
  weekStart: TimesheetWeekStart
  rounding: TimesheetRounding
  overtime: TimesheetOvertimeSettings
  expectedWeeklyMinutes: number
  gps: boolean
  reminders: TimesheetRemindersSettings
}

export const DEFAULT_TIMESHEET_SETTINGS: TimesheetSettings = {
  autoSources: {
    sessions: false,
    meetings: false,
    focus: false,
  },
  weekStart: 'MONDAY',
  rounding: 'NONE',
  overtime: {
    dailyMinutes: 0,
    weeklyMinutes: 0,
    multiplier: 1.5,
  },
  expectedWeeklyMinutes: 2400,
  gps: false,
  reminders: {
    submitReminder: false,
    approvalReminder: false,
  },
}

export interface GeoPoint {
  latitude: number
  longitude: number
  accuracy?: number
}

export interface TimeEntry {
  id: string
  workspaceId: string
  uid: string
  nodeId?: string | null
  goalId?: string | null
  start: string
  end: string
  minutes: number
  source: TimeEntrySource
  sourceRef?: string | null
  note?: string | null
  billable: boolean
  clientId?: string | null
  engagementId?: string | null
  bucket?: TimeEntryBucket
  bucketSource?: TimeEntryBucketSource
  bucketOverriddenBy?: string | null
  bucketOverriddenAt?: unknown
  geoIn?: GeoPoint | null
  geoOut?: GeoPoint | null
  status: TimeEntryStatus
  weekId: string
  tz?: string
  readAccess: string[]
  occurredAtSource?: 'CLIENT' | 'SERVER'
  auto?: boolean
  autoConfirmed?: boolean
  overlaps?: boolean
  device?: string | null
  createdAt?: unknown
  updatedAt?: unknown
}

export type ClockStateKind = 'CLOCK' | 'TIMER'

export interface ClockState {
  id?: string
  workspaceId: string
  uid: string
  kind: ClockStateKind
  nodeId?: string | null
  startedAt: string
  geoIn?: GeoPoint | null
  device?: string | null
  occurredAtSource?: 'CLIENT' | 'SERVER'
  createdAt?: unknown
}

export interface TimesheetWeekTotals {
  regularMinutes: number
  overtimeMinutes: number
  billableMinutes: number
  totalMinutes: number
}

export type LabourStatus = 'POSTED' | 'NO_COST_RATE' | 'NOT_APPLICABLE'

export interface TimesheetWeek {
  id: string // `${uid}_${weekId}`
  workspaceId: string
  uid: string
  weekId: string
  regularMinutes: number
  overtimeMinutes: number
  billableMinutes: number
  totalMinutes: number
  status: TimesheetWeekStatus
  approverUid?: string | null
  rejectNote?: string | null
  reopenedBy?: string | null
  reopenReason?: string | null
  reopenedAt?: unknown
  readAccess: string[]
  labourStatus?: LabourStatus | null
  labourExpenseIds?: string[]
  labourMinor?: number
  approvalSeq?: number
  actualMinutesDeltas?: Record<string, number> | null
  updatedAt?: unknown
}

export interface ReviewWeekResult {
  success: boolean
  decision: 'APPROVE' | 'REJECT'
  count: number
  labourStatus?: LabourStatus | null
  labourMinor?: number
  overCategoryLimit?: 'monthly' | 'quarterly' | 'yearly' | null
}

export interface ListUnpostedLabourInput {
  workspaceId: string
}

export interface UnpostedLabourWeek {
  uid: string
  displayName?: string | null
  weekId: string
  minutes: number
  totalMinutes: number
}

export interface ListUnpostedLabourResult {
  success: boolean
  weeks: UnpostedLabourWeek[]
}

export interface PostPendingLabourInput {
  workspaceId: string
  uid?: string
  weekIds?: string[]
}

export interface PostPendingLabourResult {
  success: boolean
  results: Array<{
    weekId: string
    uid: string
    success: boolean
    labourStatus: string
    labourMinor?: number
    reason?: string
  }>
}

export interface BackfillActualMinutesInput {
  workspaceId: string
}

export interface BackfillActualMinutesResult {
  success: boolean
  nodesUpdated: number
}

export interface MemberRateHistoryEntry {
  costRate: number
  billRate?: number | null
  currency: string
  effectiveFrom: string
  createdAt?: string
  createdBy?: string
}

export interface MemberRate {
  id: string // `${workspaceId}_${uid}`
  workspaceId: string
  uid: string
  costRate: number
  billRate?: number | null
  currency: string
  effectiveFrom: string
  history: MemberRateHistoryEntry[]
  updatedAt?: unknown
  updatedBy?: string
}

export interface ResolvedMemberRate {
  costRate: number
  billRate: number | null
  currency: string
  effectiveFrom: string
}

export interface ClientRateHistoryEntry {
  rate: number
  currency: string
  effectiveFrom: string
  createdAt?: string
  createdBy?: string
}

export interface ClientRate {
  id: string // `${workspaceId}_${clientId}`
  workspaceId: string
  clientId: string
  rate: number
  currentRate?: number
  currency: string
  effectiveFrom: string
  history: ClientRateHistoryEntry[]
  updatedAt?: unknown
  updatedBy?: string
}
